import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Loader2, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { AdminPage, PillTabs } from '../../../components/design-system/admin';
import { useCrmApi } from '../../../common/hooks/use-crm-api.hook';
import { AdminNotFoundPage } from '../not-found/admin-not-found.page';
import { CrmPipelineTab } from './crm-pipeline.tab';
import { CrmContactsTab } from './crm-contacts.tab';
import { CrmClientsTab } from './crm-clients.tab';
import { CrmPartnersTab } from './crm-partners.tab';
import { CrmInvestorsTab } from './crm-investors.tab';
import { CrmPressTab } from './crm-press.tab';

type Tab = 'pipeline' | 'contacts' | 'clients' | 'partners' | 'investors' | 'press';
/** `label` is an i18n key. */
const TABS: { value: Tab; label: string }[] = [
  { value: 'pipeline', label: 'ADMIN_CRM.TABS.PIPELINE' },
  { value: 'contacts', label: 'ADMIN_CRM.TABS.CONTACTS' },
  { value: 'clients', label: 'ADMIN_CRM.TABS.CLIENTS' },
  { value: 'partners', label: 'ADMIN_CRM.TABS.PARTNERS' },
  { value: 'investors', label: 'ADMIN_CRM.TABS.INVESTORS' },
  { value: 'press', label: 'ADMIN_CRM.TABS.PRESS' },
];

/**
 * Internal sales CRM, at /crm. The API answers only the super-admins in
 * CRM_ALLOWED_EMAILS, and only they see its entry under Customers in the menu.
 * Anyone else gets the portal's ordinary not-found page, so the route gives nothing away.
 */
export const CrmPage: React.FC = () => {
  const { t } = useTranslation();
  const api = useCrmApi();
  const [access, setAccess] = useState<'checking' | 'allowed' | 'denied'>('checking');
  const [params, setParams] = useSearchParams();
  const tab = (TABS.find((t) => t.value === params.get('tab'))?.value || 'pipeline') as Tab;
  // Bumped after an import so the open tab remounts and refetches.
  const [version, setVersion] = useState(0);
  const [importing, setImporting] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  useEffect(() => {
    api.checkAccess().then(() => setAccess('allowed')).catch(() => setAccess('denied'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const importFile = async (file?: File) => {
    if (!file) return;
    setImporting(true);
    try {
      const report = await api.importLegacy(JSON.parse(await file.text()));
      const parts = Object.entries(report).map(([kind, r]) => `${kind} ${r.upserted}${r.skipped.length ? ` (${t('ADMIN_CRM.IMPORT.SKIPPED', { count: r.skipped.length })})` : ''}`);
      const skipped = Object.values(report).flatMap((r) => r.skipped);
      if (skipped.length) console.warn('CRM import — skipped rows', skipped);
      toast.success(t('ADMIN_CRM.IMPORT.DONE', { parts: parts.join(' · ') }));
      setVersion((v) => v + 1);
    } catch (e: any) {
      toast.error(e?.message || t('ADMIN_CRM.IMPORT.FAILED'));
    } finally {
      setImporting(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  };

  if (access === 'checking') {
    return (
      <div className="flex justify-center p-16">
        <Loader2 className="size-6 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (access === 'denied') return <AdminNotFoundPage />;

  return (
    <AdminPage
      title={t('ADMIN_CRM.TITLE')}
      description={t('ADMIN_CRM.DESCRIPTION')}
      actions={
        <>
          <input ref={fileInput} type="file" accept="application/json,.json" className="hidden" onChange={(e) => importFile(e.target.files?.[0])} />
          <Button variant="outline" size="sm" disabled={importing} onClick={() => fileInput.current?.click()}>
            {importing ? <Loader2 className="animate-spin" /> : <Upload />}
            {t('ADMIN_CRM.IMPORT.BUTTON')}
          </Button>
        </>
      }
    >
      <PillTabs
        value={tab}
        options={TABS.map((x) => ({ value: x.value, label: t(x.label) }))}
        onChange={(next) => setParams({ tab: next }, { replace: true })}
        ariaLabel={t('ADMIN_CRM.SECTIONS')}
      />
      <div key={`${tab}-${version}`}>
        {tab === 'pipeline' && <CrmPipelineTab />}
        {tab === 'contacts' && <CrmContactsTab />}
        {tab === 'clients' && <CrmClientsTab />}
        {tab === 'partners' && <CrmPartnersTab />}
        {tab === 'investors' && <CrmInvestorsTab />}
        {tab === 'press' && <CrmPressTab />}
      </div>
    </AdminPage>
  );
};

export default CrmPage;
